//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import { useOtSelector, useOtDispatch } from '@react/redux/store'
import { OtStoreState } from '@react/redux/store'
import { CompanyProfileCTO } from '@shared/CompanyProfileCTO'
import { PoolAccountAccessPair } from '@shared/SchemaTypes'
import { EthAddr } from '@shared/SchemaTypes'
import { fetchAllPoolAccountAccessPairs } from '@react/fetchers/PoolFetchers'
import { allowPoolParticipation } from '@react/signedTxs/allowPoolParticipationTx'
import { removePoolParticipation } from '@react/signedTxs/removePoolParticipationTx'
import { poolActions } from '@react/redux/poolSlice'
import { logOT } from '@shared/loggingShared'
import VaultAccessItem from './VaultAccessItem'
import { onPromise } from '@react/components/UiHelpers'

interface VaultAccessInfo {
  poolAddr: EthAddr
  poolName: string
  hasAccess: boolean
}

interface ManageVaultAccessModalProps {
  open: boolean
  onClose: () => void
  companyProfile: CompanyProfileCTO
  onSuccess?: (updatedProfile: CompanyProfileCTO) => void
}

export default function ManageVaultAccessModal({
  open,
  onClose,
  companyProfile,
  onSuccess,
}: ManageVaultAccessModalProps): React.ReactElement {
  const [searchTerm, setSearchTerm] = useState('')
  const [vaultAccessList, setVaultAccessList] = useState<VaultAccessInfo[]>([])
  const [selectedVaults, setSelectedVaults] = useState<Set<EthAddr>>(new Set())
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [_poolAccountAccessPairs, setPoolAccountAccessPairs] = useState<PoolAccountAccessPair[]>([])

  const {
    stageConfiguration: { restAPIKey, serverApiBaseUrl },
    // @ts-ignore
    userConnection: { signer },
    webSocket,
    chainConfiguration: { chainConfigurationName },
  } = useOtSelector((state: OtStoreState) => state.chainState)

  const poolOverviewCTOs = useOtSelector((state: OtStoreState) => state.poolState.poolOverviewCTOs)
  const dispatch = useOtDispatch()

  // Load vault access data when modal opens
  useEffect(() => {
    if (open && serverApiBaseUrl && restAPIKey) {
      loadVaultAccessData()
    }
  }, [open, serverApiBaseUrl, restAPIKey, companyProfile.uuid])

  const loadVaultAccessData = async () => {
    setIsLoading(true)
    setError(null)
    try {
      // Fetch all pool account access pairs
      const allAccessPairs = await fetchAllPoolAccountAccessPairs(serverApiBaseUrl, restAPIKey)
      setPoolAccountAccessPairs(allAccessPairs)

      // Create vault access info for each pool
      const vaultList: VaultAccessInfo[] = Object.entries(poolOverviewCTOs).map(([poolAddr, poolOverview]) => {
        // Check if any of the company's wallets have access to this pool
        const hasAccess = companyProfile.wallets.some((wallet) =>
          allAccessPairs.some(
            (pair) =>
              pair.accountAddr.toLowerCase() === wallet.toLowerCase() &&
              pair.poolAddr.toLowerCase() === poolAddr.toLowerCase()
          )
        )

        return {
          poolAddr: poolAddr as EthAddr,
          poolName: poolOverview.name,
          hasAccess,
        }
      })

      setVaultAccessList(vaultList)

      // Initialize selected vaults with currently accessible ones
      const currentlyAccessible = new Set(vaultList.filter((vault) => vault.hasAccess).map((vault) => vault.poolAddr))
      setSelectedVaults(currentlyAccessible)
    } catch (err) {
      logOT.error('Error loading vault access data:', err)
      setError('Failed to load vault access data')
    } finally {
      setIsLoading(false)
    }
  }

  const handleVaultToggle = (poolAddr: EthAddr) => {
    const newSelected = new Set(selectedVaults)
    if (newSelected.has(poolAddr)) {
      newSelected.delete(poolAddr)
    } else {
      newSelected.add(poolAddr)
    }
    setSelectedVaults(newSelected)
  }

  const handleSelectAll = () => {
    if (selectedVaults.size === filteredVaults.length) {
      // Deselect all
      setSelectedVaults(new Set())
    } else {
      // Select all filtered vaults
      setSelectedVaults(new Set(filteredVaults.map((vault) => vault.poolAddr)))
    }
  }

  /* 
   Compare the current access list with the selected vaults.
   For any vaults that are in the selected vaults, grant access.
   For any vaults that are not in the selected vaults, revoke access.
  */
  const handleSave = async () => {
    if (!signer || !webSocket) {
      setError('Missing required configuration for saving vault access')
      return
    }

    setIsSaving(true)
    setError(null)

    try {
      // Determine which vaults need access changes
      const currentAccess = new Set(vaultAccessList.filter((v) => v.hasAccess).map((v) => v.poolAddr))
      const newAccess = selectedVaults

      // Vaults that need to be granted access
      const toGrant = Array.from(newAccess).filter((poolAddr) => !currentAccess.has(poolAddr))

      // Vaults that need to be revoked access
      const toRevoke = Array.from(currentAccess).filter((poolAddr) => !newAccess.has(poolAddr))

      if (toGrant.length === 0 && toRevoke.length === 0) {
        onClose()
        return
      }

      // Grant access for each vault and each wallet in the company
      for (const poolAddr of toGrant) {
        for (const wallet of companyProfile.wallets) {
          try {
            await allowPoolParticipation(webSocket, signer, poolAddr, wallet, chainConfigurationName)
            logOT.info(`Granted vault access: ${poolAddr} for wallet ${wallet}`)
          } catch (err) {
            logOT.error(`Failed to grant vault access: ${poolAddr} for wallet ${wallet}:`, err)
            // Continue with other wallets/vaults even if one fails
          }
        }
      }

      // Revoke access for each vault and each wallet in the company
      for (const poolAddr of toRevoke) {
        for (const wallet of companyProfile.wallets) {
          try {
            await removePoolParticipation(webSocket, signer, poolAddr, wallet, chainConfigurationName)
            logOT.info(`Revoked vault access: ${poolAddr} for wallet ${wallet}`)
          } catch (err) {
            logOT.error(`Failed to revoke vault access: ${poolAddr} for wallet ${wallet}:`, err)
            // Continue with other wallets/vaults even if one fails
          }
        }
      }

      // Refresh the data to reflect changes
      await loadVaultAccessData()

      // Dispatch action to refresh pool account access pairs in the store
      const updatedAccessPairs = await fetchAllPoolAccountAccessPairs(serverApiBaseUrl, restAPIKey)
      dispatch(poolActions.addPoolAccountAccessPairs({ poolAccountAccessPairs: updatedAccessPairs }))

      onSuccess?.(companyProfile)
      onClose()
    } catch (err) {
      logOT.error('Error saving vault access:', err)
      setError('Failed to save vault access changes')
    } finally {
      setIsSaving(false)
    }
  }

  const filteredVaults = vaultAccessList.filter(
    (vault) =>
      vault.poolName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vault.poolAddr.toLowerCase().includes(searchTerm.toLowerCase())
  )

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="body2" component="div">
          Manage Vault Access
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Select vaults to grant access to. Uncheck vaults to revoke access. Previously whitelisted vaults are
          pre-selected.
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            placeholder="Search vaults by name or address..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              backgroundColor: 'background.muted',
              border: '1px solid background.subtle',
              borderRadius: 2,
              px: 2,
              py: 1,
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedVaults.size === filteredVaults.length && filteredVaults.length > 0}
                  indeterminate={selectedVaults.size > 0 && selectedVaults.size < filteredVaults.length}
                  onChange={handleSelectAll}
                />
              }
              label="Select All Vaults"
            />
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
            {filteredVaults.map((vault, index) => (
              <Box key={vault.poolAddr}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedVaults.has(vault.poolAddr)}
                      onChange={() => handleVaultToggle(vault.poolAddr)}
                    />
                  }
                  label={
                    <VaultAccessItem
                      poolAddr={vault.poolAddr}
                      poolName={vault.poolName}
                      onCopyAddress={() => {}} // No copy functionality needed in modal
                      showCopyButton={false}
                    />
                  }
                  sx={{ width: '100%', m: 0, py: 1, alignItems: 'flex-start' }}
                />
                {index < filteredVaults.length - 1 && <Divider />}
              </Box>
            ))}

            {filteredVaults.length === 0 && !isLoading && (
              <Typography variant="body2" color="text.secondary" textAlign="center" py={4}>
                No vaults found matching your search
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          onClick={onPromise(handleSave)}
          variant="contained"
          disabled={isSaving || isLoading}
          startIcon={isSaving ? <CircularProgress size={20} /> : null}
        >
          {isSaving ? 'Saving...' : 'Update Access'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
